/**
 * Builds an OPEN horizontal sine-like wave path (cubic Béziers, one per half wave).
 * Starts `overscan` px left of the stage and ends `overscan` px right of it,
 * so marquee wrapping always happens off-screen.
 */
export interface WaveOptions {
  width: number;
  midY: number;
  amplitude: number;
  wavelength: number;
  /** Phase shift in px (moves crests horizontally). */
  phase: number;
  overscan: number;
}

/** Hand-tuned variation per half wave (width factor, amplitude factor). Deterministic, not random. */
const WIDTH_VAR = [1.0, 1.22, 0.86, 1.12, 0.94, 1.3, 0.9, 1.08];
const AMP_VAR = [0.8, 1.0, 0.62, 0.92, 0.72, 1.06, 0.68, 0.88];

export function buildWavePath({ width, midY, amplitude, wavelength, phase, overscan, seed = 0 }: WaveOptions & { seed?: number }): string {
  // Organic wave: extremum-to-extremum cubic segments with horizontal tangents
  // at every crest/trough (always C1-smooth), varying widths and heights.
  const half = wavelength / 2;
  const startX = -overscan - (phase % wavelength);
  const endX = width + overscan;
  let i = seed;
  let up = true;
  let x = startX;
  let y = midY - AMP_VAR[i % AMP_VAR.length] * amplitude;
  let d = `M ${x.toFixed(1)} ${y.toFixed(1)}`;
  while (x < endX) {
    i += 1;
    up = !up;
    const h = half * WIDTH_VAR[i % WIDTH_VAR.length];
    const a = amplitude * AMP_VAR[i % AMP_VAR.length];
    const nx = x + h;
    const ny = up ? midY - a : midY + a;
    // Slightly asymmetric handles so crests broaden/flatten unevenly.
    const k1 = 0.42 + (i % 3) * 0.05;
    const k2 = 0.42 + ((i + 1) % 3) * 0.05;
    d += ` C ${(x + h * k1).toFixed(1)} ${y.toFixed(1)} ${(nx - h * k2).toFixed(1)} ${ny.toFixed(1)} ${nx.toFixed(1)} ${ny.toFixed(1)}`;
    x = nx;
    y = ny;
  }
  return d;
}

/** Arc length of an SVG path string, measured with a detached SVG element. */
export function measurePathLength(d: string): number {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.style.position = 'absolute';
  svg.style.visibility = 'hidden';
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', d);
  svg.appendChild(p);
  document.body.appendChild(svg);
  const len = p.getTotalLength();
  svg.remove();
  return len;
}